import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { isStaff } from "../utils/access";
import ForcePasswordChange from "../pages/auth/ForcePasswordChange";

/** Template §9. The three-state gate — without the `loading` branch, every reload flashes /login. */
export default function ProtectedRoute() {
  const { user, loading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex vh-100 align-items-center justify-content-center">
        <span className="spinner-border text-primary" role="status" aria-label="Checking your session" />
      </div>
    );
  }

  // `state.from` lets the login page send the user back where they were headed — essential for
  // bookmarked report URLs, which is most of how this panel gets used.
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  // A FORCED PASSWORD CHANGE OUTRANKS EVERYTHING BELOW, INCLUDING THE STAFF CHECK.
  // `requirePasswordChanged` 403s every business route while the flag is set, so no other screen in
  // this panel can load anything. Rendered IN PLACE of the Outlet rather than redirected to, so
  // there is no route to bypass: every protected path shows this and only this until it is cleared.
  if (mustChangePassword) return <ForcePasswordChange />;

  // A customer account authenticates successfully but has no business here: every Module 14 route is
  // gated `staffGate` and would 403. Refusing at the shell gives a clear message instead of a wall
  // of failed requests.
  if (!isStaff(user)) {
    return (
      <div className="d-flex vh-100 align-items-center justify-content-center p-4">
        <div className="text-center" style={{ maxWidth: 420 }}>
          <i className="bi bi-shield-lock" style={{ fontSize: 32, opacity: 0.5 }} />
          <h2 className="h6 mt-3">This panel is for staff accounts</h2>
          <p className="text-muted small mb-0">
            Reports and dashboards are an internal surface. Your account should use the customer portal.
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
